import { type FormEvent, useState } from "react";
import type { AiGatewayAdvice, AiGatewayClient } from "./client";
import {
  AI_MANAGER_LOOKBACK_DAYS,
  AI_MANAGER_PURPOSES,
  aiAdviceStatusCopy,
  type AiManagerLookbackDays,
  type AiManagerPurpose,
} from "./managerAdvisor";

interface AiManagerAdvisorPanelProps {
  client: AiGatewayClient;
}

function purposeLabel(purpose: AiManagerPurpose): string {
  return AI_MANAGER_PURPOSES.find((item) => item.id === purpose)?.label ?? purpose;
}

export function AiManagerAdvisorPanel({ client }: AiManagerAdvisorPanelProps) {
  const [purpose, setPurpose] = useState<AiManagerPurpose>("farm_overview");
  const [lookbackDays, setLookbackDays] = useState<AiManagerLookbackDays>(30);
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<AiGatewayAdvice>();
  const [busy, setBusy] = useState(false);
  const [requestError, setRequestError] = useState<string>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setRequestError(undefined);

    try {
      const advice = await client.generateAdvice({
        purpose,
        lookbackDays,
        ...(question.trim() ? { question: question.trim() } : {}),
      });
      setResult(advice);
    } catch {
      setResult(undefined);
      setRequestError("ارتباط با AI Gateway انجام نشد. داده‌های عملیاتی سالن تغییری نکرده‌اند.");
    } finally {
      setBusy(false);
    }
  }

  const statusCopy = result ? aiAdviceStatusCopy(result.status) : undefined;

  return (
    <section className="section-card" aria-labelledby="ai-manager-advisor-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M7</p>
          <h2 id="ai-manager-advisor-title">مشاور مدیریتی هوش مصنوعی</h2>
        </div>
        <span className="count-pill">فقط‌خواندنی</span>
      </div>

      <p className="section-note">
        تحلیل فقط بر پایه evidence تأییدشده V2 انجام می‌شود. این بخش هیچ تغییری در وضعیت تانک، تغذیه،
        درمان، تکثیر، مالی یا کارهای سالن ایجاد نمی‌کند.
      </p>

      <form className="ai-settings-form" onSubmit={submit}>
        <div className="operation-field-grid">
          <label>
            نوع تحلیل
            <select
              value={purpose}
              onChange={(event) => setPurpose(event.target.value as AiManagerPurpose)}
            >
              {AI_MANAGER_PURPOSES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            بازه بررسی
            <select
              value={lookbackDays}
              onChange={(event) =>
                setLookbackDays(Number(event.target.value) as AiManagerLookbackDays)
              }
            >
              {AI_MANAGER_LOOKBACK_DAYS.map((days) => (
                <option key={days} value={days}>
                  {days} روز اخیر
                </option>
              ))}
            </select>
          </label>
        </div>

        <label>
          سؤال مدیر
          <textarea
            value={question}
            maxLength={2000}
            rows={4}
            placeholder={"مثلاً در " + purposeLabel(purpose) + " چه مواردی نیاز به توجه دارد؟"}
            onChange={(event) => setQuestion(event.target.value)}
          />
        </label>

        <button type="submit" className="primary-button" disabled={busy}>
          {busy ? "در حال تحلیل…" : "تحلیل با هوش مصنوعی"}
        </button>
      </form>

      {requestError ? <p className="settings-warning">{requestError}</p> : null}

      {statusCopy ? (
        <p className={statusCopy.tone === "danger" ? "settings-warning" : "section-note"}>
          {statusCopy.message}
        </p>
      ) : null}

      {result?.status === "ok" && result.advice ? (
        <div className="manager-card" aria-live="polite">
          <div className="section-title-row">
            <div>
              <p className="section-kicker">نتیجه تحلیل</p>
              <h3>{result.advice.summary}</h3>
            </div>
          </div>

          {result.advice.findings.length > 0 ? (
            <div>
              <strong>یافته‌ها</strong>
              <ul>
                {result.advice.findings.map((finding) => (
                  <li key={finding}>{finding}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.advice.recommendations.length > 0 ? (
            <div className="work-list">
              {result.advice.recommendations.map((recommendation, index) => (
                <article
                  className="work-item pending"
                  key={String(index) + "-" + recommendation.text}
                >
                  <span className="work-main">
                    <strong>{recommendation.text}</strong>
                    {recommendation.generalGuidance ? (
                      <span>راهنمای عمومی</span>
                    ) : recommendation.evidenceRefs.length > 0 ? (
                      <span>
                        شواهد:{" "}
                        {recommendation.evidenceRefs.map((ref) => (
                          <code key={ref}>{ref} </code>
                        ))}
                      </span>
                    ) : null}
                  </span>
                </article>
              ))}
            </div>
          ) : null}

          {result.advice.missingData.length > 0 ? (
            <div>
              <strong>داده‌های موردنیاز</strong>
              <ul>
                {result.advice.missingData.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <p className="section-note">
            {result.provider ? "Provider: " + result.provider : "Provider: نامشخص"}
            {result.model ? " · Model: " + result.model : ""}
            {result.runId ? " · Run ID: " + result.runId : ""}
          </p>
        </div>
      ) : null}
    </section>
  );
}
